import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const CustomerListPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Clients / Authorities');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await parivartanAPI.listCustomers();
      setRows(response.data?.data?.customers || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'customer',
    noun: 'client',
    plural: 'clients',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'customer' });

  const tableRows = useMemo(
    () => rows.map((row) => ({
      ...row,
      shortLabel: row.shortName || '—',
      panLabel: row.pan || '—',
      cityLabel: [row.city, row.state].filter(Boolean).join(', ') || '—'
    })),
    [rows]
  );

  const columns = useMemo(
    () => [
      { key: 'customerNo', label: 'Cust no.', width: '130px', mobileTitle: true },
      { key: 'category', label: 'Category', width: '180px' },
      {
        key: 'name',
        label: 'Name',
        render: (value, row) => (
          <div className="cust-name">
            <span className="cust-name__title">{value}</span>
            {row.shortName ? <span className="cust-name__meta">{row.shortName}</span> : null}
          </div>
        )
      },
      { key: 'panLabel', label: 'PAN', width: '140px' },
      { key: 'currency', label: 'Currency', width: '100px' },
      { key: 'cityLabel', label: 'Location', width: '160px' },
      revisionColumn,
      {
        key: 'status',
        label: 'Status',
        width: '120px',
        render: (value) => (
          <span className={`cust-status cust-status--${value === 'active' ? 'active' : 'inactive'}`}>
            {value === 'active' ? 'Active' : 'Inactive'}
          </span>
        )
      }
    ],
    []
  );

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Clients / Authorities"
        help="customers"
        subtitle="Authorities and clients Dhruv bids for. Use this list when a tender is captured."
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/masters/customers/new')}>
              New
            </Button>
          </>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="customers.download"
        title="Clients / Authorities"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search number, name, PAN, city…"
        pageSize={12}
        onView={(row) => navigate(`/apps/tender/masters/customers/${row._id}`)}
        onEdit={(row) => navigate(`/apps/tender/masters/customers/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' },
          { type: 'delete', label: 'Delete', variant: 'danger' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default CustomerListPage;
